import { missingField } from 'app/constants';
import React, { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { RequirementsRow } from '../../TableComponents/TableComponents';
import { usePromiseTracker } from 'react-promise-tracker';
import { placementSummaryActions } from '../../../../../../../../../store/redux-store/placement-summary/slice';
import { AddOnContext } from '../../../PlacementSummaryTab';
import { useRequirementFilter } from '../../../Helpers/requirementFilter';
import { TableSection } from '../../../customComponents/TableSection';
import { getRequirementCheck } from '../../../helper';
import { Box, Chip, Grid, MenuItem, MenuList, Typography } from '@mui/material';
import { ff_placement_ui_requirement_check } from 'app/constants/FeatureFlags/Placement/Keys';
import { useDecision } from '@optimizely/react-sdk';
import {
  selectInterviewInfo,
  selectServiceFailures,
  selectSubmissionInfo,
} from 'store/redux-store/placement-summary/selectors';
import { makeStyles } from 'tss-react/mui';
import { VirtualInterviewHistory } from '../../InterviewStatus/VirtualInterview/VirtualInterviewHistory';
import {
  IVICardView,
  IVIInterviewSubCardItemProps,
  RenderToolTipListWithTooltip,
} from '../../InterviewStatus/VirtualInterview/InterviewSpecs';
import { StatusChip } from '../../CustomComponents/StatusChip';
import { RowContainer } from '@AMIEWEB/Candidate/Common/RowContainer';
import { CustomGrid, CustomTooltip } from '@AMIEWEB/Common';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { theme } from 'styles/global-styles';
import { ICandidateInterview } from '@AMIEWEB/Candidate/CandidateProfile/CandidateTabPanel/InterviewsTab/InterviewsTab';
import {
  colorTokenMap,
  RenderWithTooltip,
} from '@AMIEWEB/Candidate/CandidateProfile/CandidateTabPanel/InterviewsTab/InterviewCard/InterviewCard';
import moment from 'moment';
import { GenericDialog } from '@AMIEWEB/Alerts/GenericDialog';
import { getEmploymentHistoryHeaders } from '@AMIEWEB/Candidate/CandidateProfile/CandidateTabPanel/InterviewsTab/helpers';
import { convertToPST } from 'app/helpers/dateHelper';

const useStyles = makeStyles()({
  title: {
    fontWeight: 500,
  },
  header: {
    fontSize: '12px',
    fontWeight: 500,
    paddingRight: '5px',
  },
  container: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    justifyContent: 'space-between',
  },
  titleCell: {
    textAlign: 'start',
    fontWeight: 500,
  },
  actionColumn: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    height: 25,
    gap: 12,
  },
  headCell: {
    fontSize: 12,
    fontWeight: 450,
  },
  cardHeadCell: {
    minWidth: 100,
  },
  valueCell: {
    fontSize: 14,
  },
  tooltip: {
    padding: '12px 0',
  },
  iconColor: {
    color: theme.palette.system.black,
  },
  chip: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: 'fit-content',
    whiteSpace: 'pre',
    fontWeight: 400,
  },
  dialogContainer: {
    width: '80%',
    maxWidth: '80%',
  },
  dialogContentRoot: {
    padding: '1.5rem',
    fontWeight: 500,
  },
});

const defaultHistoryProps = {
  rows: [],
  open: false,
  inviteId: undefined,
  interviewDate: undefined,
};

export const VirtualInterview = () => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const [requirementCheckFlag] = useDecision(ff_placement_ui_requirement_check);
  const {
    virtualInterview: {
      order: {
        type,
        data,
        view: { rating, vaAutoOffer, skillset, matchingSkillset },
      },
      candidate: interviews,
    },
  } = useSelector(selectInterviewInfo);
  const { requirementFilter } = useSelector(selectSubmissionInfo);
  const [historyModalProps, setHistoryModalProps] = useState(defaultHistoryProps);

  const { promiseInProgress: promiseInProgressVirtualInterview } = usePromiseTracker({
    area: 'placement-interview-info-vi-requirement',
    delay: 0,
  });
  const { promiseInProgress } = usePromiseTracker({
    area: 'pplacement-submission-mice-details-order-requirement',
    delay: 0,
  });
  const { virtualInterviewRequirement: serviceFailed } = useSelector(selectServiceFailures);
  const { placementId } = useContext(AddOnContext);
  const passFailType = requirementCheckFlag.enabled && data?.virtualRatingSubmissionRequirement ? type : null;
  const { display } = useRequirementFilter('submission-order', requirementFilter, passFailType);

  return display ? (
    <RequirementsRow
      id="placement-submission-order-virtual-interview"
      title={t('placement.profile.summary.interview.virtualInterviewContainer.title')}
      requirementCell={
        requirementCheckFlag.enabled && data?.virtualRatingSubmissionRequirement ? (
          <RowKey
            data={data}
            virtualInterviewRating={rating}
            virtualInterviewAutoOffer={vaAutoOffer}
            virtualInterviewSkillset={skillset}
            virtualInterviewMatchingSkillset={matchingSkillset}
            virtualInterviewType={type}
            classes={classes}
            t={t}
          />
        ) : (
          <Typography variant="body1">{missingField}</Typography>
        )
      }
      loading={requirementCheckFlag.enabled ? promiseInProgressVirtualInterview : promiseInProgress}
      hasError={serviceFailed}
      candidateCell={
        <RowValue
          interviews={interviews}
          historyModalProps={historyModalProps}
          setHistoryModalProps={setHistoryModalProps}
          t={t}
        />
      }
      onTryAgain={() =>
        requirementCheckFlag.enabled
          ? dispatch(placementSummaryActions.getVirtualInterviewRequirement({ placementId, isRetryingService: true }))
          : dispatch(placementSummaryActions.getMiscDetailsOrderRequirement({ placementId, isRetryingService: true }))
      }
    />
  ) : null;
};

const RowKey = ({
  data,
  virtualInterviewRating,
  virtualInterviewAutoOffer,
  virtualInterviewSkillset,
  virtualInterviewMatchingSkillset,
  virtualInterviewType,
  classes,
  t,
}) => {
  const items = React.useMemo(() => {
    const virtualInterviewItems = [
      {
        primaryContent:
          data && Object.keys(data).length > 0 ? (
            <Grid direction="column" spacing={4}>
              <Grid item className={classes.title}>
                {t('placement.profile.summary.interview.virtualInterviewContainer.title')}
              </Grid>
              <Grid item>
                <span className={classes.header}>
                  {t('placement.profile.summary.interview.virtualInterviewContainer.orderSpecs.rating')}
                </span>
                <span>{virtualInterviewRating}</span>
              </Grid>
              <Grid item>
                <span className={classes.header}>
                  {t('placement.profile.summary.interview.virtualInterviewContainer.orderSpecs.vaAutoOffer')}
                </span>
                <span>{virtualInterviewAutoOffer}</span>
              </Grid>
              <Grid item>
                <span className={classes.header}>
                  {t('placement.profile.summary.interview.virtualInterviewContainer.orderSpecs.skillset')}
                </span>
                <span>{virtualInterviewSkillset}</span>
              </Grid>
              <Grid item>
                <span className={classes.header}>
                  {t('placement.profile.summary.interview.virtualInterviewContainer.orderSpecs.matchingSkillset')}
                </span>
                <span>{virtualInterviewMatchingSkillset}</span>
              </Grid>
            </Grid>
          ) : (
            missingField
          ),
        icon: virtualInterviewType ? getRequirementCheck(virtualInterviewType) : null,
      },
    ] as {
      icon?: any;
      primaryContent?: any;
      secondaryContent?: any;
    }[];
    return virtualInterviewItems;
  }, [
    virtualInterviewRating,
    virtualInterviewAutoOffer,
    virtualInterviewSkillset,
    virtualInterviewMatchingSkillset,
    virtualInterviewType,
    classes,
    t,
  ]);
  return <TableSection items={items} />;
};

const ScheduleItem: React.FC<IVIInterviewSubCardItemProps> = ({ interViewDetails }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const [isDialogOpen, setDialogOpen] = useState<boolean>(false);
  const [dialogData, setDialogData] = useState<ICandidateInterview | null>();

  const getRows = data => {
    // eslint-disable-next-line array-callback-return
    const interviewRows: any = [];
    if (dialogData) {
      data.history.forEach(item => {
        const row = {
          fieldChanged: (
            <span>
              {`${item?.inviteId ? `${item.inviteId} - ` : ''}`}
              <span style={{ fontWeight: 'bold' }}>{item.field}</span>
            </span>
          ),
          oldValue: item.oldValue,
          newValue: item.newValue,
          changedBy: item.changedBy,
          date: `${convertToPST(item.changedDate)}`,
        };
        interviewRows.push(row);
      });
    }
    return interviewRows;
  };

  const handleClick = (data: ICandidateInterview) => {
    setDialogData(data);
    setDialogOpen(true);
  };

  const getSkillset = () => {
    const renderText = interViewDetails?.discipline
      ? interViewDetails?.specialtyAbbr
        ? `${interViewDetails?.discipline} - ${interViewDetails?.specialtyAbbr}`
        : interViewDetails?.discipline
      : missingField;
    return <RenderWithTooltip text={renderText} maxLength={30} className={classes.titleCell} />;
  };

  const chipData = {
    color: colorTokenMap[interViewDetails?.candidateInterviewStatus?.color] || colorTokenMap.tertiaryGrey,
    background: colorTokenMap[interViewDetails?.candidateInterviewStatus?.background] || colorTokenMap.platinum,
    label: interViewDetails?.candidateInterviewStatus?.label || 'Unavailable',
  };

  const mostRecentReason = interViewDetails?.candidateInterviewReasons?.find(r => r.isMostRecent);
  const { tooltipContent } = useMemo(() => {
    return {
      tooltipContent: (
        <RowContainer>
          <tr>
            <td className={classes.headCell}>
              {t('placement.profile.summary.interview.virtualInterviewContainer.interviewSpecs.interviewDate')}
            </td>
            <td className={classes.valueCell}>
              {interViewDetails?.interviewDate
                ? moment(new Date(interViewDetails.interviewDate)).format('MM/DD/YYYY')
                : missingField}
            </td>
          </tr>
          <tr>
            <td className={classes.headCell}>
              {t('placement.profile.summary.interview.virtualInterviewContainer.interviewSpecs.sentDate')}
            </td>
            <td className={classes.valueCell}>
              {interViewDetails?.invitationDate
                ? moment(new Date(interViewDetails.invitationDate)).format('MM/DD/YYYY')
                : missingField}
            </td>
          </tr>
          <tr>
            <td className={classes.headCell}>
              {t('placement.profile.summary.interview.virtualInterviewContainer.interviewSpecs.inviteId')}
            </td>
            <td className={classes.valueCell}>{interViewDetails?.inviteId ?? missingField}</td>
          </tr>
        </RowContainer>
      ),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interViewDetails]);

  return (
    <div className={classes.container}>
      <table>
        <tbody>
          <tr>
            <td colSpan={2} className={classes.titleCell}>
              <CustomTooltip isStandardContent standardMargin tooltipContent={tooltipContent} placement="bottom">
                {getSkillset()}
              </CustomTooltip>
            </td>
          </tr>
          <tr>
            <td>
              <CustomTooltip
                tooltipContent={interViewDetails?.candidateInterviewStatus?.interviewStatus ?? ''}
                interactive
                arrow
              >
                <Chip
                  className={classes.chip}
                  style={{
                    color: chipData?.color,
                    borderColor: chipData?.color,
                    background: chipData?.background,
                  }}
                  variant="outlined"
                  size="medium"
                  label={chipData?.label}
                />
              </CustomTooltip>
            </td>
          </tr>
          <tr>
            <td className={`${classes.headCell} ${classes.cardHeadCell}`}>
              {t('placement.profile.summary.interview.virtualInterviewContainer.interviewSpecs.lastUpdated')}
            </td>
            <td className={classes.valueCell} style={{ whiteSpace: 'nowrap' }}>
              {interViewDetails?.changedDate
                ? moment(new Date(interViewDetails.changedDate)).format('MM/DD/YYYY')
                : missingField}
            </td>
          </tr>
          <tr>
            <td className={`${classes.headCell} ${classes.cardHeadCell}`}>
              {t('placement.profile.summary.interview.virtualInterviewContainer.interviewSpecs.rating')}
            </td>
            <td className={classes.valueCell} style={{ whiteSpace: 'nowrap' }}>
              {interViewDetails?.interviewRating !== ''
                ? interViewDetails?.interviewRating?.substring(0, interViewDetails?.interviewRating?.length - 1)
                : missingField}
            </td>
          </tr>
          <tr>
            <td className={classes.headCell}>{t('candidate.virtualInterviewTabLabels.statusReason')}</td>
            <td className={classes.valueCell}>{mostRecentReason?.reason ?? missingField}</td>
          </tr>
          <tr>
            <td className={classes.headCell}>{t('candidate.virtualInterviewTabLabels.subStatus')}</td>
            <td className={classes.valueCell}>
              <RenderToolTipListWithTooltip values={mostRecentReason?.subReasons} />
            </td>
          </tr>
        </tbody>
      </table>

      <div className={classes.actionColumn}>
        <CustomTooltip
          standardMargin
          classes={{ tooltip: classes.tooltip }}
          contentStyle={{ alignSelf: 'center' }}
          tooltipContent={
            <TooltipContent
              onClick={() => {
                handleClick(interViewDetails);
              }}
            >
              {t('placement.profile.summary.interview.virtualInterviewContainer.interviewSpecs.history.viewHistory')}
            </TooltipContent>
          }
          placement={'bottom'}
        >
          <MoreVertIcon className={classes.iconColor} />
        </CustomTooltip>
        <GenericDialog
          variant={'blue'}
          fullWidth
          classes={{
            paper: classes.dialogContainer,
          }}
          open={isDialogOpen}
          disablePortal
          disableEscapeKeyDown={false}
          dialogTitleProps={{
            text: `Virtual Interview - View History`,
            closeButton: true,
          }}
          onClose={() => setDialogOpen(false)}
          dialogContentProps={{
            classes: { root: classes.dialogContentRoot },
          }}
        >
          <>
            <Box>
              {`Invite: ${dialogData?.inviteId ? dialogData.inviteId : '-'}`}
              {' ,  '}
              {`Date: ${
                dialogData?.interviewDate ? moment(new Date(dialogData.interviewDate)).format('MM/DD/YYYY') : '--'
              }`}
            </Box>
            <CustomGrid
              columns={getEmploymentHistoryHeaders()}
              rows={getRows(dialogData)}
              gridName={'InterviewHistoryGrid'}
              defaultPageView={'25'}
              fixedItemsPerPage={25}
              pageOptions={[25, 50, 100, 200]}
              disableGutters
              noPadding
              centerHoverView={false}
              showCount={false}
              minCellWidth={'100px'}
            />
          </>
        </GenericDialog>
      </div>
    </div>
  );
};

const TooltipContent = ({ onClick, ...props }) => {
  return (
    <MenuList disablePadding>
      <MenuItem dense onClick={onClick}>
        {props.children}
      </MenuItem>
    </MenuList>
  );
};

const RowValue = ({ interviews, t }) => {
  const items = React.useMemo(() => {
    return [
      {
        primaryContent: !interviews.length ? (
          <Typography variant="body1">
            {t('placement.profile.summary.interview.virtualInterviewContainer.interviewSpecs.skillNotMatching')}
          </Typography>
        ) : (
          <Grid container spacing={2}>
            {interviews.map((cardView, index) => (
              <Grid item key={index.toString()} xs={12}>
                <ScheduleItem interViewDetails={cardView} />
              </Grid>
            ))}
          </Grid>
        ),
      },
    ];
  }, [interviews, t]);
  return <TableSection items={items} />;
};
